import { PropsWithChildren, useEffect, useMemo } from 'react';

import {
  useListCampaignFundraisers,
  useListEvents,
  useListStoreItems,
  useShowStoreCause,
} from '@givebacks/api/store';
import { useShowCauseW_Selector } from 'core/Api';
import { useUpdateCauseUrl } from 'core/Hooks';
import { Navigate, useMatch, useNavigate, useParams } from 'react-router-dom';
import { LoadingOverlay } from 'ui';

import { CauseContext } from '../CauseContext';

const selectTotal = (data: { meta: { total?: number } }) => data.meta.total;
const selectStoreEnabled = (data: { payments_enabled: boolean; store_active: boolean }) =>
  data?.payments_enabled && data?.store_active;

// Guard component that validates the cause exists
const CauseGuard = ({ children }: PropsWithChildren) => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const subpath = useMatch('/causes/:uuid/:subpath/*');

  const {
    data: cause,
    isLoading,
    isError,
  } = useShowCauseW_Selector({
    uuid,
    enabled: !!uuid,
  });

  const { data: storeEnabled = false } = useShowStoreCause({
    causeId: cause?.uuid,
    queryOptions: {
      options: {
        include: 'store_active',
      },
    },
    select: selectStoreEnabled,
  });

  const { data: storeItemsCount = 0, isLoading: isLoadingStoreItems } = useListStoreItems({
    causeId: cause?.uuid,
    _enabled: !!cause?.uuid,
    queryOptions: {
      searches: {
        type: {
          value: 'StoreItems::Item,StoreItems::Membership',
          by: 'in',
        },
        in_store: {
          value: true,
        },
        live: {
          value: true,
        },
      },
      options: {
        total: true,
      },
      order: {
        field: 'order',
        direction: 'asc',
      },
      join: 'AND',
    },
    pagination: {
      limit: 24,
    },
    select: selectTotal,
  });

  const { data: campaignFundraisersCount = 0, isLoading: isLoadingCampaignFundraisers } =
    useListCampaignFundraisers({
      causeId: cause?.uuid,
      _enabled: !!cause?.uuid,
      queryOptions: {
        options: {
          credits: true,
          total: true,
        },
        order: {
          field: 'expires_at',
          direction: 'asc',
        },
        searches: {
          live: {
            value: true,
          },
          type: {
            value: 'StoreItems::Campaign,StoreItems::Athon,StoreItems::RallyUp',
            by: 'in',
          },
        },
      },
      select: selectTotal,
    });

  const { data: eventsCount = 0, isLoading: isLoadingEvents } = useListEvents({
    causeId: cause?.uuid,
    queryOptions: {
      searches: {
        live: {
          value: true,
        },
      },
      options: {
        include: 'event_dates',
        total: true,
      },
    },
    select: selectTotal,
  });

  useUpdateCauseUrl({ cause_shortener: cause?.shortener });

  const currentTab = useMemo(() => {
    return subpath?.params.subpath ?? 'about';
  }, [subpath?.params.subpath]);

  const isTabDataLoading = useMemo(() => {
    return isLoadingStoreItems || isLoadingCampaignFundraisers || isLoadingEvents;
  }, [isLoadingStoreItems, isLoadingCampaignFundraisers, isLoadingEvents]);

  const ctx = useMemo(() => {
    const showStoreTab = storeItemsCount > 0 && storeEnabled;
    const showFundraisersTab = campaignFundraisersCount > 0;
    const showTicketsTab = eventsCount > 0;

    return {
      showStoreTab,
      showFundraisersTab,
      showTicketsTab,
    };
  }, [storeItemsCount, storeEnabled, campaignFundraisersCount, eventsCount]);

  useEffect(() => {
    if (isTabDataLoading) return;

    const isShopTabInvalid = currentTab === 'shop' && !ctx.showStoreTab;
    const isFundraisersTabInvalid = currentTab === 'campaigns' && !ctx.showFundraisersTab;
    const isTicketsTabInvalid = currentTab === 'events' && !ctx.showTicketsTab;

    if ((isShopTabInvalid || isFundraisersTabInvalid || isTicketsTabInvalid) && cause) {
      navigate('about', { replace: true });
    }
  }, [
    cause,
    currentTab,
    navigate,
    isTabDataLoading,
    ctx.showStoreTab,
    ctx.showFundraisersTab,
    ctx.showTicketsTab,
  ]);

  // Handle loading state
  if (isLoading) {
    return <LoadingOverlay visible />;
  }

  // Handle error state
  if (isError || !cause) {
    return <Navigate to="/causes" replace />;
  }

  // Provide the cause data to children
  return <CauseContext.Provider value={{ ...ctx, cause }}>{children}</CauseContext.Provider>;
};

export { CauseGuard };
